<template>
	<UInput
		ref="inputRef"
		:label="t('get-fiber.form.phone-number.label')"
		:value="displayValue"
		:type="TEL"
		placeholder="(555) 555-5555"
		maxlength="14"
		class="w-full"
		:autocomplete="PHONE_AUTOCOMPLETE"
		@input="handleInput"
		@paste="handlePaste"
		@blur="handleBlur"
	/>
</template>

<script setup lang="ts">
import { PHONE_AUTOCOMPLETE } from '@/constants/form/autocompletes.js'
import { TEL } from '@/constants/form/text-inputs/types.js'
import {
	formatPhoneNumber,
	parsePhoneNumber,
	unformatPhoneNumber
} from '@/utils/formatPhone'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({
	useScope: 'global'
})

const props = defineProps({
	id: {
		type: String,
		required: true
	},
	modelValue: {
		type: String,
		default: ''
	}
})

const emit = defineEmits<{
	'update:modelValue': [value: string]
}>()

const inputRef = ref<HTMLInputElement>()
const displayValue = ref('')

const handleInput = (event: Event) => {
	const input = event.target as HTMLInputElement
	const cursorPosition = input.selectionStart || 0
	const prevLength = displayValue.value.length

	// Parse and format the input
	const parsed = parsePhoneNumber(input.value)
	const formatted = formatPhoneNumber(input.value)

	displayValue.value = formatted

	// Emit the clean 10 digits
	emit('update:modelValue', parsed.digits)

	// Adjust cursor position after formatting
	nextTick(() => {
		if (inputRef.value) {
			const newLength = formatted.length
			const diff = newLength - prevLength

			// Smart cursor positioning
			let newPosition = cursorPosition + diff

			// If user is deleting, keep cursor in place
			if (diff < 0 && cursorPosition > 0) {
				newPosition = Math.max(0, cursorPosition)
			}

			// Don't place cursor on formatting characters
			const formattingChars = [ '(', ')', ' ', '-' ]
			while (formattingChars.includes(formatted[newPosition - 1] ?? '') && newPosition > 0) {
				newPosition--
			}

			if (inputRef.value) {
				// UInput ref gives component instance, so get underlying input
				const raw = (inputRef.value as any).$el ?? inputRef.value
				const nativeInput = raw instanceof HTMLInputElement ? raw : raw.querySelector('input')
				nativeInput?.setSelectionRange(newPosition, newPosition)
			}
		}
	})
}

const handlePaste = (event: ClipboardEvent) => {
	event.preventDefault()
	const pastedText = event.clipboardData?.getData('text') || ''

	// Handle various paste formats
	const parsed = parsePhoneNumber(pastedText)
	const formatted = formatPhoneNumber(pastedText)

	displayValue.value = formatted
	emit('update:modelValue', parsed.digits)
}

const handleBlur = () => {
	// Ensure proper formatting on blur
	const formatted = formatPhoneNumber(displayValue.value)
	displayValue.value = formatted
}

// Watch for external changes to modelValue
watch(() => props.modelValue, newValue => {
	if (newValue !== unformatPhoneNumber(displayValue.value)) {
		displayValue.value = formatPhoneNumber(newValue)
	}
})

// Initialize
displayValue.value = formatPhoneNumber(props.modelValue)
</script>
